import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Truck, Package2, MapPin, Calendar, Plus, ExternalLink, CheckCircle, Clock, Camera, AlertTriangle, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useInventory } from '../context/InventoryContext';
import { supabase } from '../lib/supabase';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import { Location } from '../types';
import Badge from '../components/ui/Badge';

interface PurchaseOrder {
  id: string;
  order_number: string;
  supplier_id: string;
  total_amount: number;
  suppliers: {
    name: string;
  };
}

interface OrderItem {
  id: string;
  product_id: string;
  quantity: number;
  received_quantity: number;
  unit_price: number;
  status: string;
  supplies: {
    name: string;
    sku: string;
  };
}

const ReceivingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addTransaction } = useInventory();
  const [selectedPO, setSelectedPO] = useState<string>('');
  const [locations, setLocations] = useState<Location[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [receivingData, setReceivingData] = useState<Record<string, {
    quantity: number;
    condition: string;
    location_id: string;
    lotNumber: string;
    notes: string;
    images: string[];
  }>>({});

  useEffect(() => {
    loadPurchaseOrders();
    loadLocations();
  }, []);

  useEffect(() => {
    if (selectedPO) {
      loadOrderItems();
    }
  }, [selectedPO]);

  const loadLocations = async () => {
    try {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('is_active', true)
        .order('is_default', { ascending: false })
        .order('name');

      if (error) throw error;
      setLocations(data || []);
    } catch (error) {
      console.error('Error loading locations:', error);
      setError('Failed to load locations');
    }
  };

  const loadPurchaseOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('purchase_orders')
        .select(`
          id,
          order_number,
          supplier_id,
          total_amount,
          suppliers (
            name
          )
        `)
        .in('status', ['ordered', 'partial'])
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPurchaseOrders(data || []);
    } catch (error) {
      console.error('Error loading purchase orders:', error);
      setError('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const loadOrderItems = async () => {
    try {
      const { data, error } = await supabase
        .from('purchase_order_items')
        .select(`
          id,
          product_id,
          quantity,
          received_quantity,
          unit_price,
          status,
          supplies (
            name,
            sku
          )
        `)
        .eq('purchase_order_id', selectedPO);

      if (error) throw error;
      setOrderItems(data || []);
      
      // Initialize receiving data for each item
      const initialData: Record<string, any> = {};
      data?.forEach(item => {
        initialData[item.id] = {
          quantity: 0,
          condition: 'good',
          location_id: locations.find(l => l.is_default)?.id || '',
          lotNumber: '',
          notes: '',
          images: []
        };
      });
      setReceivingData(initialData);
    } catch (error) {
      console.error('Error loading order items:', error);
      setError('Failed to load order items');
    }
  };

  const handleImageUpload = async (itemId: string, file: File) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${itemId}-${Date.now()}.${fileExt}`;
      const filePath = `receiving-images/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('inventory')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('inventory')
        .getPublicUrl(filePath);

      setReceivingData(prev => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          images: [...prev[itemId].images, publicUrl]
        }
      }));
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Failed to upload image');
    }
  };

  const handleReceive = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate receiving data
      const invalidItems = Object.entries(receivingData).filter(([itemId, data]) => {
        const orderItem = orderItems.find(item => item.id === itemId);
        return data.quantity > (orderItem?.quantity || 0) - (orderItem?.received_quantity || 0);
      });

      if (invalidItems.length > 0) {
        setError('Some items exceed remaining quantity');
        return;
      }

      // Update received quantities and create transaction records
      for (const [itemId, data] of Object.entries(receivingData)) {
        if (data.quantity <= 0) continue;

        const orderItem = orderItems.find(item => item.id === itemId);
        if (!orderItem) continue;

        // Update order item
        // Get the purchase order details for proper metadata
        const purchaseOrder = purchaseOrders.find(po => po.id === selectedPO);
        const poNumber = purchaseOrder?.order_number || selectedPO;
        const supplierName = purchaseOrder?.suppliers?.name || 'Unknown Supplier';

        const newReceivedQty = (orderItem.received_quantity || 0) + data.quantity;
        const newStatus = newReceivedQty >= orderItem.quantity ? 'received' : 'partial';

        const { error: updateError } = await supabase
          .from('purchase_order_items')
          .update({
            received_quantity: newReceivedQty,
            status: newStatus
          })
          .eq('id', itemId);

        if (updateError) throw updateError;

        // Create transaction and update stock through InventoryContext
        if (data.location_id) {
          const { error: locationError } = await supabase.rpc('update_product_location_stock', {
            p_product_id: orderItem.product_id,
            p_location_id: data.location_id,
            p_quantity_change: data.quantity,
            p_transaction_type: 'in'
          });

          if (locationError) console.warn('Location stock update failed:', locationError);
        }

        await addTransaction({
          product_id: orderItem.product_id,
          type: 'in',
          quantity: data.quantity,
          notes: data.notes || `Received from ${poNumber}`,
          status: 'completed',
          currency: 'USD',
          metadata: {
            location_id: data.location_id,
            purchase_order_id: selectedPO,
            purchase_order_number: poNumber,
            supplier_name: supplierName,
            condition: data.condition,
            lot_number: data.lotNumber,
            receiving_notes: data.notes || '',
            images: data.images
          }
        });
      }

      // Check if all items are received
      const allReceived = orderItems.every(item => 
        (item.received_quantity || 0) + (receivingData[item.id]?.quantity || 0) >= item.quantity
      );

      // Update purchase order status
      const { error: poError } = await supabase
        .from('purchase_orders')
        .update({
          status: allReceived ? 'received' : 'partial'
        })
        .eq('id', selectedPO);

      if (poError) throw poError;

      // Reset form and reload data
      setSelectedPO('');
      setOrderItems([]);
      setReceivingData({});
      loadPurchaseOrders();
    } catch (error) {
      console.error('Error processing receiving:', error);
      setError('Failed to process receiving');
    } finally {
      setLoading(false);
    }
  };

  const purchaseOrderOptions = purchaseOrders.map(po => ({
    value: po.id,
    label: `${po.order_number} - ${po.suppliers.name} ($${po.total_amount.toFixed(2)})`
  }));

  const conditionOptions = [
    { value: 'good', label: 'Good' },
    { value: 'damaged', label: 'Damaged' },
    { value: 'partial', label: 'Partial' }
  ];

  const locationOptions = [
    { value: '', label: 'Select Location' },
    ...locations.map(location => ({ value: location.id, label: location.name }))
  ];

  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className="overflow-hidden rounded-lg">
        <div className="relative">
          <img
            src="https://images.pexels.com/photos/4481259/pexels-photo-4481259.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
            alt="Warehouse receiving dock with incoming shipment processing"
            className="w-full h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30 flex items-center">
            <div className="px-8 text-white">
              <h1 className="text-3xl font-bold mb-2">Receiving</h1>
              <p className="text-lg opacity-90">
                Streamline incoming inventory and purchase order processing
              </p>
              <div className="mt-4 flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Package2 className="h-4 w-4 mr-2" />
                  <span>Incoming Inventory</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  <span>Quality Control</span>
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-2" />
                  <span>Real-time Processing</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Receiving Center</CardTitle>
            <Link to="/locations" className="text-sm text-blue-600 hover:underline">
              Manage Locations
            </Link>
          </div>
        </CardHeader>
        <CardContent>
        <CardHeader>
          <CardTitle>Select Purchase Order</CardTitle>
        </CardHeader>
        <CardContent>
          <Select
            options={[
              { value: '', label: 'Select Purchase Order' },
              ...purchaseOrderOptions
            ]}
            value={selectedPO}
            onChange={(value) => setSelectedPO(value)}
            fullWidth
          />
        </CardContent>
        </CardContent>
      </Card>

      {selectedPO && orderItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Receive Items</CardTitle>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="mb-4 bg-red-50 text-red-600 p-4 rounded-md flex items-center">
                <AlertTriangle className="h-5 w-5 mr-2" />
                {error}
              </div>
            )}

            <div className="space-y-6">
              {orderItems.map(item => {
                const remainingQty = item.quantity - (item.received_quantity || 0);
                const isFullyReceived = remainingQty <= 0;

                return (
                  <div key={item.id} className="border-b border-gray-200 pb-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          {item.supplies.name}
                        </h3>
                        <p className="text-sm text-gray-500">SKU: {item.supplies.sku}</p>
                      </div>
                      <Badge variant={isFullyReceived ? 'success' : 'warning'}>
                        {isFullyReceived ? 'Fully Received' : `${remainingQty} remaining`}
                      </Badge>
                    </div>

                    {!isFullyReceived && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Quantity to Receive"
                          type="number"
                          min="0"
                          max={remainingQty}
                          value={receivingData[item.id]?.quantity || 0}
                          onChange={(e) => setReceivingData(prev => ({
                            ...prev,
                            [item.id]: {
                              ...prev[item.id],
                              quantity: parseInt(e.target.value) || 0
                            }
                          }))}
                          fullWidth
                        />

                        <Select
                          label="Receiving Location"
                          options={locationOptions}
                          value={receivingData[item.id]?.location_id || ''}
                          onChange={(value) => setReceivingData(prev => ({
                            ...prev,
                            [item.id]: {
                              ...prev[item.id],
                              location_id: value
                            }
                          }))}
                          fullWidth
                        />

                        <Select
                          label="Condition"
                          options={conditionOptions}
                          value={receivingData[item.id]?.condition || 'good'}
                          onChange={(value) => setReceivingData(prev => ({
                            ...prev,
                            [item.id]: {
                              ...prev[item.id],
                              condition: value
                            }
                          }))}
                          fullWidth
                        />

                        {!receivingData[item.id]?.location_id && (
                          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
                            <div className="flex items-start">
                              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 mr-2" />
                              <div>
                                <p className="font-medium">Location Required</p>
                                <p>Please select a location where the received items will be stored.</p>
                              </div>
                            </div>
                          </div>
                        )}

                        <Input
                          label="Lot/Serial Number"
                          value={receivingData[item.id]?.lotNumber || ''}
                          onChange={(e) => setReceivingData(prev => ({
                            ...prev,
                            [item.id]: {
                              ...prev[item.id],
                              lotNumber: e.target.value
                            }
                          }))}
                          fullWidth
                        />

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Photos
                          </label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              id={`photo-${item.id}`}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  handleImageUpload(item.id, file);
                                }
                              }}
                            />
                            <label
                              htmlFor={`photo-${item.id}`}
                              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
                            >
                              <Camera className="h-4 w-4 inline-block mr-2" />
                              Add Photo
                            </label>
                            {receivingData[item.id]?.images?.map((url, index) => (
                              <div key={index} className="relative">
                                <img
                                  src={url}
                                  alt={`Receiving photo ${index + 1}`}
                                  className="h-10 w-10 object-cover rounded"
                                />
                                <button
                                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                                  onClick={() => {
                                    setReceivingData(prev => ({
                                      ...prev,
                                      [item.id]: {
                                        ...prev[item.id],
                                        images: prev[item.id].images.filter((_, i) => i !== index)
                                      }
                                    }));
                                  }}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Notes
                          </label>
                          <textarea
                            rows={3}
                            value={receivingData[item.id]?.notes || ''}
                            onChange={(e) => setReceivingData(prev => ({
                              ...prev,
                              [item.id]: {
                                ...prev[item.id],
                                notes: e.target.value
                              }
                            }))}
                            className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="flex justify-end space-x-2">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSelectedPO('');
                    setOrderItems([]);
                    setReceivingData({});
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReceive}
                  disabled={loading || Object.values(receivingData).every(data => !data.quantity) || 
                           Object.values(receivingData).some(data => data.quantity > 0 && !data.location_id)}
                >
                  Process Receiving
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReceivingPage;